import React, { useState, useEffect } from 'react';
import { FiRefreshCw, FiTarget } from 'react-icons/fi';
import { getMyTasks } from '../../../api/retailPerformanceTasks';
import PerformanceTaskCard from '../../../components/retail/performance-tasks/PerformanceTaskCard';
import LoadingSpinner from '../../../components/common/LoadingSpinner';

const MyTasksPage = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('active');

  useEffect(() => {
    fetchMyTasks();
    const interval = setInterval(fetchMyTasks, 30000);
    return () => clearInterval(interval);
  }, [filter]);

  const fetchMyTasks = async () => {
    try {
      const statusFilter =
        filter === 'all'
          ? undefined
          : filter === 'active'
            ? ['active', 'paused', 'scheduled']
            : [filter];

      const response = await getMyTasks({ status: statusFilter });
      if (response.success) setTasks(response.data || []);
    } catch (error) {
      console.error('Error fetching my tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const activeTasks = tasks.filter((t) => ['active', 'paused'].includes(t.status));
  const scheduledTasks = tasks.filter((t) => t.status === 'scheduled');
  const completedTasks = tasks.filter((t) => ['completed', 'failed', 'ended'].includes(t.status));

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">My Performance Tasks</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            View your assigned tasks and track progress
          </p>
        </div>
        <button
          onClick={fetchMyTasks}
          className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <FiRefreshCw /> Refresh
        </button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-green-50 dark:bg-green-900 dark:bg-opacity-20 rounded-lg p-4 border-2 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-600 dark:text-green-400">Active Tasks</p>
              <p className="text-2xl font-bold text-green-700 dark:text-green-300">
                {activeTasks.length}
              </p>
            </div>
            <FiTarget className="w-8 h-8 text-green-500" />
          </div>
        </div>
        <div className="bg-blue-50 dark:bg-blue-900 dark:bg-opacity-20 rounded-lg p-4 border-2 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-600 dark:text-blue-400">Scheduled</p>
              <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">
                {scheduledTasks.length}
              </p>
            </div>
            <FiTarget className="w-8 h-8 text-blue-500" />
          </div>
        </div>
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border-2 border-gray-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Completed</p>
              <p className="text-2xl font-bold text-gray-700 dark:text-gray-300">
                {completedTasks.length}
              </p>
            </div>
            <FiTarget className="w-8 h-8 text-gray-500" />
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="mb-6 flex gap-2">
        {['active', 'scheduled', 'completed', 'all'].map((status) => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={`px-4 py-2 rounded-lg ${filter === status ? 'bg-accent-gold text-white' : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300'}`}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </button>
        ))}
      </div>

      {/* Task List */}
      {loading ? (
        <LoadingSpinner message="Loading my tasks..." className="py-12" />
      ) : tasks.length === 0 ? (
        <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg">
          <FiTarget className="w-16 h-16 mx-auto mb-4 text-gray-400" />
          <p className="text-gray-500">No tasks assigned to you yet</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {tasks.map((task) => (
            <PerformanceTaskCard key={task.id} task={task} showActions={false} />
          ))}
        </div>
      )}
    </div>
  );
};

export default MyTasksPage;
